import React, { useState, useRef, useEffect } from 'react';
import './VoiceInput.css';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { LexRuntimeV2Client, RecognizeUtteranceCommand } from '@aws-sdk/client-lex-runtime-v2';
import TimerHandler from './TimerHandler';

// Get configuration from environment variables
const IDENTITY_POOL_ID = process.env.REACT_APP_IDENTITY_POOL_ID || '';
const BOT_ID = process.env.REACT_APP_LEX_BOT_ID || '';
const BOT_ALIAS_ID = process.env.REACT_APP_LEX_BOT_ALIAS_ID || '';
const LOCALE_ID = process.env.REACT_APP_LEX_LOCALE_ID || 'en_US';

interface VoiceInputProps {
  onResult: (text: string) => void;
  disabled?: boolean;
}

const VoiceInput: React.FC<VoiceInputProps> = ({ onResult, disabled = false }) => {
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [availableMicrophones, setAvailableMicrophones] = useState<MediaDeviceInfo[]>([]);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string>('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const lexClientRef = useRef<LexRuntimeV2Client | null>(null);
  
  // Check browser support and permissions
  useEffect(() => {
    const checkBrowserSupport = () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Your browser does not support voice recording');
        return false;
      }
      
      if (!window.AudioContext && !(window as any).webkitAudioContext) {
        setError('Your browser does not support audio processing');
        return false;
      }
      
      return true;
    };
    
    const checkMicrophonePermission = async () => {
      try {
        // Request permission
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        // Stop all tracks immediately
        stream.getTracks().forEach(track => track.stop());
        
        return true;
      } catch (err) {
        console.error('Error requesting microphone permission:', err);
        setError('Microphone permission denied');
        return false;
      }
    };
    
    const initializeVoiceInput = async () => {
      if (checkBrowserSupport()) {
        await checkMicrophonePermission();
      }
    };
    
    initializeVoiceInput();
  }, []);
  
  // Get available microphones
  useEffect(() => {
    const getAvailableMicrophones = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
          console.warn('enumerateDevices not supported in this browser');
          return;
        }
        
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter(device => device.kind === 'audioinput');
        console.log('Available microphones:', microphones);
        
        setAvailableMicrophones(microphones);
        
        // Select the first microphone by default
        if (microphones.length > 0 && !selectedMicrophoneId) {
          setSelectedMicrophoneId(microphones[0].deviceId);
        }
      } catch (err) {
        console.error('Error getting available microphones:', err);
      }
    };
    
    getAvailableMicrophones();
  }, [selectedMicrophoneId]);
  
  // Initialize the Lex client
  useEffect(() => {
    const initLexClient = async () => {
      try {
        console.log('Initializing Lex client...');
        console.log('Using Identity Pool ID:', IDENTITY_POOL_ID);
        setIsInitializing(true);
        
        // Set up Cognito Identity credentials
        const cognitoIdentityClient = new CognitoIdentityClient({
          region: 'us-east-1'
        });
        
        const credentials = fromCognitoIdentityPool({
          client: cognitoIdentityClient,
          identityPoolId: IDENTITY_POOL_ID
        });
        
        // Create Lex client with proper configuration
        lexClientRef.current = new LexRuntimeV2Client({
          region: 'us-east-1',
          credentials,
          maxAttempts: 3,
          retryMode: 'standard'
        });
        
        console.log('Lex client initialized successfully');
        setIsInitializing(false);
      } catch (err) {
        console.error('Error initializing Lex client:', err);
        setError('Failed to initialize voice recognition');
        setIsInitializing(false);
      }
    };
    
    initLexClient();
  }, []);
  
  // Start recording
  const startListening = async () => {
    try {
      setError(null);
      
      // Check if browser supports getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Your browser does not support voice recording');
      }
      
      // Check if Lex client is initialized
      if (!lexClientRef.current) {
        throw new Error('Voice recognition is not initialized yet');
      }
      
      // Check supported MIME types
      const supportedMimeTypes = [
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/ogg;codecs=opus',
        'audio/wav',
        'audio/mp4'
      ].filter(mimeType => MediaRecorder.isTypeSupported(mimeType));
      
      console.log('Supported MIME types:', supportedMimeTypes);
      
      if (supportedMimeTypes.length === 0) {
        throw new Error('No supported audio MIME types found');
      }
      
      // Use the first supported MIME type
      const mimeType = supportedMimeTypes[0];
      console.log('Using MIME type:', mimeType);
      
      // Get audio stream with selected microphone
      const constraints = {
        audio: selectedMicrophoneId 
          ? { deviceId: { exact: selectedMicrophoneId } } 
          : true
      };
      
      console.log('Using audio constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Create media recorder with the supported MIME type
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 16000 // Match Lex's expected sample rate
      });
      
      // Clear previous audio chunks
      audioChunksRef.current = [];
      
      // Set up event handlers
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Combine audio chunks into a single blob
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        console.log('Recorded audio blob:', audioBlob.size, 'bytes, type:', audioBlob.type);
        
        try {
          await sendAudioToLex(audioBlob);
        } catch (err) {
          console.error('Error sending audio to Lex:', err);
          if (err instanceof Error) {
            setError(`Failed to process voice input: ${err.message}`);
          } else {
            setError('Failed to process voice input');
          }
        }
        
        // Stop all tracks in the stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      mediaRecorderRef.current = mediaRecorder;
      setIsListening(true);
    } catch (err) {
      console.error('Error starting voice recording:', err);
      if (err instanceof Error) {
        setError(`Failed to start voice recording: ${err.message}`);
      } else {
        setError('Failed to start voice recording');
      }
    }
  };
  
  // Stop recording
  const stopListening = () => {
    if (mediaRecorderRef.current && isListening) {
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  };
  
  // Send audio to Lex
  const sendAudioToLex = async (audioBlob: Blob) => {
    if (!lexClientRef.current) {
      throw new Error('Lex client not initialized');
    }
    
    try {
      console.log('Converting audio to PCM format...');
      
      // Save original audio for debugging
      const audioUrl = URL.createObjectURL(audioBlob);
      console.log('Original audio URL (for debugging):', audioUrl);
      console.log('Original audio type:', audioBlob.type);
      
      // Convert audio to PCM format
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContext();
      const audioArrayBuffer = await audioBlob.arrayBuffer();
      console.log('Audio blob size:', audioBlob.size, 'bytes');
      console.log('Audio array buffer size:', audioArrayBuffer.byteLength, 'bytes');
      
      const audioBuffer = await audioContext.decodeAudioData(audioArrayBuffer);
      console.log('Audio decoded successfully. Duration:', audioBuffer.duration, 'seconds');
      console.log('Sample rate:', audioBuffer.sampleRate, 'Hz');
      console.log('Channels:', audioBuffer.numberOfChannels);
      
      // Get PCM data using AudioProcessor service for better conversion
      import('../services/AudioProcessor').then(async ({ default: AudioProcessor }) => {
        try {
          const pcmData = await AudioProcessor.convertToLexFormat(audioBlob);
          console.log('PCM data size from AudioProcessor:', pcmData.byteLength, 'bytes');
          
          // Continue with the Lex request using the properly formatted PCM data
          await sendPcmToLex(pcmData);
        } catch (err) {
          console.error('Error using AudioProcessor:', err);
          // Fallback to direct conversion
          const pcmData = audioBufferToPCM(audioBuffer);
          console.log('PCM data size from direct conversion:', pcmData.byteLength, 'bytes');
          await sendPcmToLex(pcmData);
        }
      }).catch(err => {
        console.error('Error importing AudioProcessor:', err);
        // Fallback to direct conversion
        const pcmData = audioBufferToPCM(audioBuffer);
        console.log('PCM data size from direct conversion:', pcmData.byteLength, 'bytes');
        return sendPcmToLex(pcmData);
      });
      
      // Return early as the actual Lex request will be handled by sendPcmToLex
      return;
    } catch (err) {
      console.error('Error preparing audio for Lex:', err);
      if (err instanceof Error) {
        setError(`Failed to process voice input: ${err.message}`);
      } else {
        setError('Failed to process voice input');
      }
      throw err;
    }
  };
  
  // Send PCM data to Lex
  const sendPcmToLex = async (pcmData: ArrayBuffer) => {
    if (!lexClientRef.current) {
      throw new Error('Lex client not initialized');
    }
    
    try {
      
      console.log('Sending audio to Lex...');
      console.log('Bot ID:', BOT_ID);
      console.log('Bot Alias ID:', BOT_ALIAS_ID);
      console.log('Locale ID:', LOCALE_ID);
      
      // Create a session ID that's consistent for the same user
      const sessionId = `session-${Date.now()}`;
      console.log('Session ID:', sessionId);
      
      // Create the command with the correct parameters
      const command = new RecognizeUtteranceCommand({
        botId: BOT_ID,
        botAliasId: BOT_ALIAS_ID,
        localeId: LOCALE_ID,
        sessionId: sessionId,
        requestContentType: 'audio/x-l16; sample-rate=16000; channel-count=1',
        inputStream: new Uint8Array(pcmData)
      });
      
      // Send the command
      console.log('Sending command to Lex...');
      
      // Add timeout for the request
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Lex request timed out after 10 seconds')), 10000);
      });
      
      // Race between the actual request and the timeout
      const response = await Promise.race([
        lexClientRef.current.send(command),
        timeoutPromise
      ]) as any;
      
      console.log('Received response from Lex:', response);
      
      // Create a detailed log of the response structure
      console.log('Lex response structure:', Object.keys(response));
      
      // Add more detailed logging
      if (response.inputTranscript) {
        console.log('Lex transcription:', response.inputTranscript);
        onResult(response.inputTranscript);
      } else {
        console.warn('No inputTranscript in Lex response');
        setError('No speech detected or recognized');
      }
      
    } catch (err) {
      console.error('Error sending audio to Lex:', err);
      if (err instanceof Error) {
        setError(`Failed to process voice input: ${err.message}`);
      } else {
        setError('Failed to process voice input');
      }
      throw err;
    }
  };
  
  // Convert AudioBuffer to PCM format
  const audioBufferToPCM = (audioBuffer: AudioBuffer): Uint8Array => {
    // Lex expects 16-bit PCM audio at 16kHz sample rate, single channel
    const sampleRate = audioBuffer.sampleRate;
    const numChannels = audioBuffer.numberOfChannels;
    const length = audioBuffer.length;
    
    console.log(`Converting audio: ${numChannels} channels, ${sampleRate}Hz, ${length} samples`);
    
    // Get audio data from the first channel
    const audioData = audioBuffer.getChannelData(0);
    
    // Resample to 16kHz if needed
    let resampledData = audioData;
    if (sampleRate !== 16000) {
      console.log('Resampling audio to 16kHz...');
      // Simple resampling (not high quality but works for testing)
      const resampleRatio = 16000 / sampleRate;
      const resampledLength = Math.floor(length * resampleRatio);
      resampledData = new Float32Array(resampledLength);
      
      for (let i = 0; i < resampledLength; i++) {
        const originalIndex = Math.floor(i / resampleRatio);
        resampledData[i] = audioData[originalIndex];
      }
    }
    
    // Convert to 16-bit PCM
    const pcmData = new Int16Array(resampledData.length);
    for (let i = 0; i < resampledData.length; i++) {
      // Convert float audio data (-1.0 to 1.0) to 16-bit PCM (-32768 to 32767)
      pcmData[i] = Math.max(-32768, Math.min(32767, resampledData[i] * 32767));
    }
    
    // Convert to Uint8Array
    return new Uint8Array(pcmData.buffer);
  };
  
  return (
    <div className="voice-input">
      {availableMicrophones.length > 1 && (
        <select 
          className="microphone-select"
          value={selectedMicrophoneId}
          onChange={(e) => setSelectedMicrophoneId(e.target.value)}
          disabled={isListening || isInitializing}
        >
          {availableMicrophones.map(mic => (
            <option key={mic.deviceId} value={mic.deviceId}>
              {mic.label || `Microphone ${mic.deviceId.slice(0, 5)}...`}
            </option>
          ))}
        </select>
      )}
      <button
        className={`voice-button ${isListening ? 'listening' : ''} ${isInitializing ? 'initializing' : ''}`}
        onClick={isListening ? stopListening : startListening}
        disabled={disabled || isInitializing}
        aria-label={isListening ? 'Stop listening' : 'Start listening'}
        title={isListening ? 'Stop listening' : 'Start listening'}
      >
        <i className={`fas ${isListening ? 'fa-stop' : 'fa-microphone'}`}></i>
      </button>
      {error && <div className="voice-error">{error}</div>}
    </div>
  );
};

export default VoiceInput;